import { stringify } from 'query-string';
import { ImResponse, hiManagerClient, resolve } from 'services/api';

export interface TodoListResponse {
  todoInstances: Todo.Record[];
  typeCountMap: Record<Todo.Type, number>;
  todoStatusCountMap: Record<Todo.Status, number>;
}

export function todoList(params: Todo.Filter) {
  return resolve(
    hiManagerClient.get<ImResponse<TodoListResponse>>('getTodoInstanceList', {
      params,
      paramsSerializer: (params) => stringify(params, { arrayFormat: 'none' }),
    }),
  );
}

export function todoTypeList(params: { taskId: string }, signal?: AbortSignal) {
  return resolve(
    hiManagerClient.get<ImResponse<Todo.TypeList>>('getTodoTypeList', {
      params,
      signal,
    }),
  );
}

export function updateTodoTypeList(body: Pick<Todo.TypeList, 'typeOrder'> & { taskId: string }) {
  return hiManagerClient.post('updateTodoTypeOrder', body);
}

export function nonCompletedCount() {
  return resolve(hiManagerClient.get<ImResponse<{ num: number }>>('getNonCompletedCount'));
}

export function taskDetail(id: string | number) {
  return resolve(hiManagerClient.get<ImResponse<Todo.TaskDetail>>(`todo/task/${id}`));
}

export function taskList(ignoreAuth = false) {
  return resolve(
    hiManagerClient.get<ImResponse<Todo.Task[]>>('todo/task', {
      params: { ignoreAuth },
    }),
  ).then((res) => {
    return res.map((task) => {
      return {
        ...task,
        storeList: task.storeList.map((platformScopes) => {
          return {
            ...platformScopes,
            regionStoreScopes: platformScopes.regionStoreScopes.map((regionScopes) => {
              return {
                ...regionScopes,
                storeIds: regionScopes.storeIds?.map(Number),
              };
            }),
          };
        }),
      };
    });
  });
}

export function createTask(body: Omit<Todo.Task, 'id' | 'isDefault'>) {
  return resolve(hiManagerClient.post<ImResponse<Todo.TaskDetail>>('todo/task', body));
}

export function updateTask({ id, ...body }: Omit<Todo.Task, 'isDefault'>) {
  return hiManagerClient.put(`todo/task/${id}`, body);
}

export function deleteTask(id: string | number) {
  return hiManagerClient.delete(`todo/task/${id}`);
}

export function updatePolicy({ id, ...body }: Todo.Policy) {
  return hiManagerClient.put(`todo/task/policy/${id}`, body);
}

export interface UnsetStore {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  storeId: string;
  internalStoreId: number;
  alias: string;
  name: string;
}

export function unsetStore() {
  return resolve(hiManagerClient.get<ImResponse<UnsetStore[]>>('todo/task/store/unset'));
}

export interface MoveStoresBody {
  taskId: number;
  storeList: UnsetStore[];
}

export function moveStores(body: MoveStoresBody) {
  return hiManagerClient.put('todo/task/store/set', body);
}

export const batchClose = (body: { day: number }) => {
  return hiManagerClient.post('batchCloseTodoInstance', body);
};

export type ChildTodoStatus = {
  id: string;
  status: Todo.Status;
  subStatus: string;
  default: boolean;
};
// 子状态列表
export const todoStatusList = (taskId: number, signal?: AbortSignal) => {
  return resolve<ChildTodoStatus[]>(
    hiManagerClient.get('todoStatusList', {
      params: { task_id: taskId },
      signal,
    }),
  );
};

// 新增子状态
export const postTodoStatus = (body: Omit<ChildTodoStatus, 'id'>) => {
  return hiManagerClient.post('todoStatus', body);
};
// 修改子状态
export const putTodoStatus = (id: string, body: {}) => {
  return hiManagerClient.put(`todoStatus/${id}`, body);
};
// 删除子状态
export const deleteTodoStatus = (id: string) => {
  return hiManagerClient.delete(`todoStatus/${id}`);
};

// 导出待办明细
export const exportTodoTypeDetail = (params: Todo.Filter & { lang: string }) => {
  return hiManagerClient.get('exportTodoInstanceList', {
    params,
    paramsSerializer: (params) => stringify(params, { arrayFormat: 'none' }),
  });
};

// 批量关闭
export const batchCloseTodo = (data: {
  ids: string[];
  resultType?: string;
  customOperateResult?: string;
}) => {
  return hiManagerClient.post('batchCloseTodoInstanceForResult', data);
};

// 更新policy order
export const updatePolicyOrder = (body: { policyId: number; resultTypeConfig: string[] }) => {
  return hiManagerClient.post(`todo/task/policy/${body.policyId}/order`, {
    resultTypeConfig: body.resultTypeConfig,
  });
};

// 批量转交
export const batchTransferTodo = (data: { ids: string[]; operator: string }) => {
  return hiManagerClient.post('batchChangeOperatorTodo', data);
};
